/**
 * QuickOrderBar
 * ─────────────
 * Sticky, customer-facing quick-order panel that sits at the top of the menu
 * for coffee shops, pizza shops, kebab/takeaway shops — basically any cuisine
 * where customers want to order in <60 seconds without browsing photos.
 *
 * Two collapse states:
 *   COLLAPSED (default) — single bar showing the three big buttons
 *     [Pickup] [Delivery] [Eat-in]   +   "Order method • Time"
 *   EXPANDED — full panel with:
 *     - Service-type toggle
 *     - "Now" / "Specific time" picker
 *     - Cart summary (live count + total from useDemoCart)
 *     - Stripe checkout CTA (POST /demo/checkout)
 *
 * The actual menu items remain interactive below the bar; everything the user
 * adds flows into the same DemoCart already used by the rest of the site.
 */
import { useState, useMemo } from "react";
import { Coffee, Bike, UtensilsCrossed, Clock, ChevronUp, ChevronDown, CreditCard, ShoppingBag } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL || (typeof window !== "undefined" ? window.location.origin : "");
const SERVICE_MODES = [
  { id: "pickup",   label: "Pickup",   icon: Coffee,           hint: "Walk in, grab and go" },
  { id: "delivery", label: "Delivery", icon: Bike,             hint: "We'll bring it" },
  { id: "eatin",    label: "Eat-in",   icon: UtensilsCrossed,  hint: "Find a seat, we'll bring it" },
];

const TIME_PRESETS = ["ASAP", "+15 min", "+30 min", "+45 min", "+1 hour", "Pick time"];

const fmt = (n) => Number(n || 0).toFixed(2);

export default function QuickOrderBar({ restaurant, cart, userId }) {
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState("pickup");
  const [timePreset, setTimePreset] = useState("ASAP");
  const [pickTime, setPickTime] = useState("");
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [paying, setPaying] = useState(false);
  const [lastOrderItems, setLastOrderItems] = useState(null);

  const lines = cart?.items || [];
  const itemCount = cart?.itemCount ?? lines.reduce((s, l) => s + (l.qty || 1), 0);
  const fallbackTotal = useMemo(
    () => lines.reduce((s, l) => s + (Number(l.price) || 0) * (l.qty || 1), 0),
    [lines]
  );
  const total = cart?.subtotal ?? fallbackTotal;

  const requestedTime = timePreset === "Pick time" ? pickTime : timePreset;

  async function checkout() {
    if (lines.length === 0) {
      toast.error("Add at least one item to your order");
      return;
    }
    if (!name.trim() || !phone.trim()) {
      toast.error("Name and phone are required");
      return;
    }
    setPaying(true);
    try {
      const r = await fetch(`${API}/api/directory/restaurants/${restaurant.slug}/pickup-order`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: name.trim(),
          phone: phone.trim(),
          mode,
          time: requestedTime,
          items: lines.map((l) => ({ name: l.name, qty: l.qty || 1, price: l.price })),
          total,
        }),
      });
      if (!r.ok) {
        const j = await r.json().catch(() => ({}));
        throw new Error(j.detail || "Order failed");
      }
      const j = await r.json();
      toast.success(`Order #${j.order?.id || "—"} sent · pay at counter or via link`);
      // Remember items so the customer can save them as their usual right after
      setLastOrderItems(lines.map((l) => ({ name: l.name, qty: l.qty || 1, price: l.price, size_chosen: l.size_chosen })));
      cart?.clear?.();
      setOpen(false);
    } catch (e) {
      toast.error(e.message || "Couldn't place order");
    } finally {
      setPaying(false);
    }
  }

  async function saveAsUsual() {
    if (!userId || !lastOrderItems) return;
    try {
      const nickname = window.prompt("Give this saved order a name", `My ${(lastOrderItems[0]?.name || "usual")}`);
      if (!nickname) return;
      const r = await fetch(`${API}/api/directory/standard-orders/${userId}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          venue_slug: restaurant.slug,
          name: nickname,
          items: lastOrderItems,
        }),
      });
      if (!r.ok) throw new Error("Couldn't save");
      toast.success("Saved as your usual — find it in your profile.");
      setLastOrderItems(null);
    } catch (e) {
      toast.error(e.message || "Couldn't save");
    }
  }

  return (
    <div
      data-testid="quick-order-bar"
      className="sticky top-16 z-20 -mx-4 px-4 py-3 mb-5 border-y backdrop-blur text-stone-900"
      style={{
        background: "rgba(255,255,255,0.96)",
        borderColor: "var(--tpl-line, #e7e5e4)",
      }}
    >
      {/* Collapsed strip */}
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        data-testid="quick-order-toggle"
        className="w-full flex items-center justify-between gap-3 text-left"
      >
        <div className="flex items-center gap-2 min-w-0">
          <div
            className="w-8 h-8 rounded-full grid place-items-center text-white shrink-0"
            style={{ background: "var(--tpl-accent, #292524)" }}
          >
            ⚡
          </div>
          <div className="min-w-0">
            <p className="text-[11px] uppercase tracking-wider font-bold opacity-70">Quick order</p>
            <p className="text-[13px] font-semibold capitalize truncate">
              {mode} · {requestedTime || "—"}
              {itemCount > 0 && <span className="ml-2 opacity-70">· {itemCount} item{itemCount === 1 ? "" : "s"} · ${fmt(total)}</span>}
            </p>
          </div>
        </div>
        {open ? <ChevronUp className="w-4 h-4 opacity-60" /> : <ChevronDown className="w-4 h-4 opacity-60" />}
      </button>

      {/* Save-as-my-usual prompt after a successful checkout */}
      {userId && lastOrderItems && lastOrderItems.length > 0 && (
        <div
          data-testid="quick-order-save-usual"
          className="mt-3 px-3 py-2 rounded-lg bg-amber-50 border border-amber-200 flex items-center gap-3"
        >
          <span className="text-[13px] font-semibold text-amber-900">⭐ Save as my usual?</span>
          <button
            data-testid="quick-order-save-usual-btn"
            onClick={saveAsUsual}
            className="ml-auto px-3 py-1 rounded-full bg-amber-600 text-white text-[12px] font-bold hover:bg-amber-500"
          >
            Save
          </button>
          <button
            onClick={() => setLastOrderItems(null)}
            className="text-[12px] text-amber-700 hover:underline"
          >
            No thanks
          </button>
        </div>
      )}

      {open && (
        <div className="mt-4 space-y-4" data-testid="quick-order-panel">          {/* Service mode */}
          <div>
            <p className="text-[11px] uppercase tracking-wider font-bold opacity-70 mb-2">How would you like it?</p>
            <div className="grid grid-cols-3 gap-2">
              {SERVICE_MODES.map((s) => {
                const Icon = s.icon;
                const active = mode === s.id;
                return (
                  <button
                    key={s.id}
                    data-testid={`quick-order-mode-${s.id}`}
                    onClick={() => setMode(s.id)}
                    className={`p-3 rounded-xl border-2 text-left transition-all ${
                      active ? "shadow-sm" : "border-stone-200 hover:border-stone-400"
                    }`}
                    style={active ? {
                      borderColor: "var(--tpl-accent, #292524)",
                      background: "var(--tpl-surface, #f5f5f4)",
                    } : {}}
                  >
                    <Icon className="w-4 h-4 mb-1" style={active ? { color: "var(--tpl-accent)" } : { color: "#78716c" }} />
                    <p className="font-semibold text-[13px]">{s.label}</p>
                    <p className="text-[10px] opacity-60 leading-tight">{s.hint}</p>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Time */}
          <div>
            <p className="text-[11px] uppercase tracking-wider font-bold opacity-70 mb-2 flex items-center gap-1">
              <Clock className="w-3 h-3" /> When?
            </p>
            <div className="flex flex-wrap gap-1.5">
              {TIME_PRESETS.map((t) => (
                <button
                  key={t}
                  data-testid={`quick-order-time-${t.replace(/\s|\+/g, "")}`}
                  onClick={() => setTimePreset(t)}
                  className={`px-3 py-1.5 rounded-full border text-[12px] font-semibold ${
                    timePreset === t
                      ? "text-white border-transparent"
                      : "bg-white border-stone-200 hover:border-stone-400"
                  }`}
                  style={timePreset === t ? { background: "var(--tpl-accent, #292524)" } : {}}
                >
                  {t}
                </button>
              ))}
            </div>
            {timePreset === "Pick time" && (
              <input
                data-testid="quick-order-pick-time"
                type="time"
                value={pickTime}
                onChange={(e) => setPickTime(e.target.value)}
                className="mt-2 px-3 py-1.5 rounded-lg border border-stone-200 text-[13px]"
              />
            )}
          </div>

          {/* Customer */}
          <div className="grid grid-cols-2 gap-2">
            <div>
              <p className="text-[11px] uppercase tracking-wider font-bold opacity-70 mb-1">Your name</p>
              <input
                data-testid="quick-order-name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Jane"
                className="w-full px-3 py-2 rounded-lg border border-stone-200 text-[13px]"
              />
            </div>
            <div>
              <p className="text-[11px] uppercase tracking-wider font-bold opacity-70 mb-1">Phone</p>
              <input
                data-testid="quick-order-phone"
                type="tel"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                placeholder="04xx xxx xxx"
                className="w-full px-3 py-2 rounded-lg border border-stone-200 text-[13px]"
              />
            </div>
          </div>

          {/* Cart summary + checkout */}
          <div
            className="rounded-xl p-3 flex items-center gap-3"
            style={{ background: "var(--tpl-surface, #f5f5f4)" }}
          >
            <ShoppingBag className="w-4 h-4 opacity-70" />
            <div className="flex-1 min-w-0 text-[13px]">
              {itemCount === 0 ? (
                <p className="opacity-70 italic">Pick items below to add to your order</p>
              ) : (
                <p>
                  <strong>{itemCount}</strong> item{itemCount === 1 ? "" : "s"} ·
                  <strong className="ml-1 tabular-nums">${fmt(total)}</strong>
                </p>
              )}
            </div>
            <Button
              data-testid="quick-order-pay"
              onClick={checkout}
              disabled={itemCount === 0 || paying}
              className="rounded-full text-white shrink-0"
              style={{ background: "var(--tpl-accent, #292524)" }}
            >
              <CreditCard className="w-3.5 h-3.5 mr-1.5" />
              {paying ? "Sending…" : "Place & pay"}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
